import { useEffect, useId, useRef, useState } from 'react'
import { MoonIcon } from './MoonIcon'

// ── Clapboard icon ────────────────────────────────────────────────────────────
// Two metaphors in one glyph:
//   arm raised + grey  = scene not yet shot (episode unwatched)
//   arm slammed + gold = "and... action's a wrap" (episode watched)
//
// <Clapboard watched={bool} /> animates the transition: the arm swings down and
// CLAPS onto the board with a spring recoil, the board flushes gold, and a
// twinkle pops at the contact point to sell the "snap." Marking-watched should
// feel like slating a take, not toggling a checkbox.
//
// Glyph ported from Design's "recolorable premium glyph" pass (2026-08-06):
// the board/arm/stripe facets are all color-mix(var(--ic), white/black) off
// the single active color (`accent` when watched, `idle` otherwise) instead
// of flat fills, so the idle-gray and watched-gold states read as the same
// dimensional object rather than two different icons. The slam/snap/spark
// mechanics below are unchanged from the original pass.
//
// ClapboardOpen / ClapboardClosed are kept as static drop-ins for any caller
// that just wants a fixed state with no animation.

const HINGE = { x: 4, y: 18 }    // arm pivot, in viewBox units
const SNAP  = { x: 41, y: 13.5 } // where the clapper meets the board on the clap
const CLAP_STRIPES = [4, 11, 18, 25, 32].map((x, i) => ({
  x1: x, x2: x + 5, x3: x + 9, x4: x + 4,
  mix: i % 2 === 0 ? 85 : 20, tone: i % 2 === 0 ? 'black' : 'white', mixTone: i % 2 === 0 ? 15 : 80,
}))

// Timing (2026-09-16, Jason: "still too fast in all instances"): the arm swing is a
// keyframe (clapArm, 0.8s with overshoot), not a CSS transition, so it plays even when
// the glyph mounts already-watched (a re-keyed row, a ring shown after a scrub). The
// whole beat is ~1.2s. `slamOnMount` runs it on first paint.
export const CLAP_MS = 1200
export function Clapboard({ size = 26, watched = false, accent = 'var(--green)', idle = 'rgba(var(--ink-rgb),0.5)', centered = false, slamOnMount = false }) {
  const [slam, setSlam] = useState(() => !!(slamOnMount && watched))
  const prev = useRef(slamOnMount ? false : watched)
  const uid = useId().replace(/:/g, '')
  const clipId = `clapClip-${uid}`

  useEffect(() => {
    // Only celebrate the unwatched -> watched transition
    if (watched && !prev.current) {
      setSlam(true)
      const t = setTimeout(() => setSlam(false), CLAP_MS)
      prev.current = watched
      return () => clearTimeout(t)
    }
    // Undone/rewound while the slam was still mid-flight (Undo tapped inside the
    // ~1.2s beat): the cleanup above cancels the pending setSlam(false), but slam
    // itself was never told to stop — so clapArm's `both` fill-mode keeps freezing
    // the arm at its closed keyframe, overriding the plain rotate(-38deg) the now-
    // unwatched state should show, until that dead timeout would have fired. Drop
    // the animation immediately so the arm's ordinary lift-back-up transition takes
    // over right away instead of a stale closed pose (found live-testing Undo).
    if (!watched && prev.current) setSlam(false)
    prev.current = watched
  }, [watched])

  const color = watched ? accent : idle
  const armRot = watched ? 0 : -38
  // Punchy overshoot when slamming down; gentle ease when lifting back up
  const armTransition = watched ? 'none' : 'transform 0.3s cubic-bezier(0.4,0,0.2,1)'

  // clapFlash/clapSnap/clapSnapDot/clapDot keyframes live in index.css — see
  // MoonIcon.jsx's comment for why an inline <style> inside <svg> is unsafe.
  return (
    <svg width={size} height={size} viewBox="0 0 48 42" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible', '--ic': color }}>
      <defs><clipPath id={clipId}><rect x="4" y="18" width="40" height="22" rx="3" /></clipPath></defs>
      <g style={{ transform: centered ? (watched ? 'translateY(-4px)' : 'translateY(2px)') : 'none', transition: 'transform 0.8s var(--ease-pop)' }}>

      {/* Flash bloom behind the board on slam */}
      {slam && (
        <circle cx="24" cy="29" r="22" fill={accent} opacity="0" style={{ transformBox: 'view-box', transformOrigin: '24px 29px', '--nxa': 'clapFlash 1.1s ease-out 0.3s forwards' }}/>
      )}

      {/* Board body */}
      <rect x="4" y="18" width="40" height="22" rx="3"
        fill="color-mix(in srgb, var(--ic) 18%, transparent)" stroke="color-mix(in srgb, var(--ic) 85%, black 12%)" strokeWidth="1.6"
        style={{ transition: 'fill 0.3s ease, stroke 0.3s ease' }}/>
      <line x1="4" y1="26" x2="44" y2="26" stroke="color-mix(in srgb, var(--ic) 70%, black 8%)" strokeWidth="1.1" opacity="0.7" style={{ transition: 'stroke 0.3s ease' }}/>
      <line x1="9" y1="31.5" x2="39" y2="31.5" stroke="color-mix(in srgb, var(--ic) 60%, transparent)" strokeWidth="1" opacity="0.55" style={{ transition: 'stroke 0.3s ease' }}/>
      <line x1="9" y1="35.5" x2="25" y2="35.5" stroke="color-mix(in srgb, var(--ic) 60%, transparent)" strokeWidth="1" opacity="0.55" style={{ transition: 'stroke 0.3s ease' }}/>

      {/* Arm, pivots about the hinge. transform-box:view-box lets transform-origin
          resolve in viewBox units so the rotation hinges exactly at HINGE. */}
      <g style={{
        transformBox: 'view-box',
        transformOrigin: `${HINGE.x}px ${HINGE.y}px`,
        transform: `rotate(${armRot}deg)`,
        transition: armTransition,
        '--nxa': slam ? 'clapArm 0.8s cubic-bezier(0.34,1.3,0.64,1) both' : 'none',
      }}>
        <rect x="4" y="10" width="40" height="7" rx="1.4"
          fill="color-mix(in srgb, var(--ic) 92%, white 8%)" stroke="color-mix(in srgb, var(--ic) 85%, black 15%)" strokeWidth="1.3"
          style={{ transition: 'fill 0.3s ease, stroke 0.3s ease' }}/>
        {CLAP_STRIPES.map((off, i) => (
          <path key={i} d={`M${off.x1} 10 L${off.x2} 17 L${off.x3} 17 L${off.x4} 10 Z`}
            style={{ fill: `color-mix(in srgb, var(--ic) ${off.mix}%, ${off.tone} ${off.mixTone}%)`, transition: 'fill 0.3s ease' }}/>
        ))}
      </g>

      <circle cx="6.4" cy="13.5" r="1.4" fill="color-mix(in srgb, var(--ic) 70%, black 30%)" style={{ transition: 'fill 0.3s ease' }}/>

      <g clipPath={`url(#${clipId})`}>
        <ellipse cx="12" cy="20" rx="16" ry="6" fill="white" opacity="0.1" transform="rotate(-18 12 20)"/>
      </g>

      {/* Snap twinkle at the clapper contact point, fires the instant the arm lands */}
      {slam && (
        <g stroke="#ffe9a8" strokeWidth="0.9" strokeLinecap="round"
          style={{ transformBox: 'view-box', transformOrigin: `${SNAP.x}px ${SNAP.y}px`, opacity: 0, '--nxa': 'clapSnap 0.6s ease-out 0.5s' }}>
          <line x1={SNAP.x} y1={SNAP.y - 4} x2={SNAP.x} y2={SNAP.y + 4}/>
          <line x1={SNAP.x - 4} y1={SNAP.y} x2={SNAP.x + 4} y2={SNAP.y}/>
          <line x1={SNAP.x - 2.8} y1={SNAP.y - 2.8} x2={SNAP.x + 2.8} y2={SNAP.y + 2.8}/>
          <line x1={SNAP.x + 2.8} y1={SNAP.y - 2.8} x2={SNAP.x - 2.8} y2={SNAP.y + 2.8}/>
        </g>
      )}
      {slam && [0, 1].map(i => (
        <circle key={i} cx={SNAP.x} cy={SNAP.y} r="1.4" fill={i ? accent : '#fff'}
          style={{ ['--sx']: `${i ? -5 : 6}px`, ['--sy']: '-7px', opacity: 0, '--nxa': 'clapSnapDot 0.7s ease-out 0.5s' }}/>
      ))}
      </g>
    </svg>
  )
}

// Open clapboard: arm tilted up ~38°, board empty (static)
export function ClapboardOpen({ size = 24, color = 'rgba(var(--ink-rgb),0.3)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2.5" y="10" width="19" height="12" rx="1.5" stroke={color} strokeWidth="1.5"/>
      <line x1="2.5" y1="14" x2="21.5" y2="14" stroke={color} strokeWidth="1.2" opacity="0.6"/>
      <line x1="5" y1="17" x2="19" y2="17" stroke={color} strokeWidth="1" opacity="0.35"/>
      <line x1="5" y1="19.5" x2="13" y2="19.5" stroke={color} strokeWidth="1" opacity="0.35"/>
      <g transform="rotate(-38, 2.5, 10)">
        <rect x="2.5" y="7" width="16" height="3" rx="0.75" stroke={color} strokeWidth="1.3" fill="none"/>
        <line x1="6"    y1="7" x2="4.5"  y2="10" stroke={color} strokeWidth="1.2"/>
        <line x1="9.5"  y1="7" x2="8"    y2="10" stroke={color} strokeWidth="1.2"/>
        <line x1="13"   y1="7" x2="11.5" y2="10" stroke={color} strokeWidth="1.2"/>
        <line x1="16.5" y1="7" x2="15"   y2="10" stroke={color} strokeWidth="1.2"/>
      </g>
    </svg>
  )
}

// Closed clapboard: arm flat against body, golden — the clap has landed (static)
export function ClapboardClosed({ size = 24, color = 'var(--amber)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2.5" y="10" width="19" height="12" rx="1.5" fill={`${color}18`} stroke={color} strokeWidth="1.5"/>
      <line x1="2.5" y1="14" x2="21.5" y2="14" stroke={color} strokeWidth="1.2" opacity="0.6"/>
      <line x1="5" y1="17" x2="19" y2="17" stroke={color} strokeWidth="1" opacity="0.5"/>
      <line x1="5" y1="19.5" x2="13" y2="19.5" stroke={color} strokeWidth="1" opacity="0.5"/>
      <rect x="2.5" y="7" width="19" height="3" rx="0.75" fill={`${color}35`} stroke={color} strokeWidth="1.3"/>
      <line x1="6"  y1="7" x2="4.5"  y2="10" stroke={color} strokeWidth="1.5"/>
      <line x1="9.5"  y1="7" x2="8"    y2="10" stroke={color} strokeWidth="1.5"/>
      <line x1="13"   y1="7" x2="11.5" y2="10" stroke={color} strokeWidth="1.5"/>
      <line x1="16.5" y1="7" x2="15"   y2="10" stroke={color} strokeWidth="1.5"/>
      <line x1="20"   y1="7" x2="18.5" y2="10" stroke={color} strokeWidth="1.5"/>
      <circle cx="3.5" cy="10" r="1.2" fill={color}/>
    </svg>
  )
}

// A clapboard that slams on its own a beat after it mounts: the "it's done" moment
// on onboarding's last step (replaces the MovieTicket cinematic from AddShowSpotlight).
// ── ClapSlamLayer: the one visible slam for every mark, anywhere ───────────────
// Mounted once (CinematicLayer). On nextep:progress-marked it floats a 96px slate
// mid-screen and slams it, ~1.2s, then fades. Rows and rings keep their tiny glyphs
// for state; this is the animation you actually see. The deck rides its own slate
// up the card, so it stamps window.__nextepClapAt and the layer stays quiet.
export function ClapSlamLayer() {
  const [on, setOn] = useState(false)
  const t = useRef(null)
  useEffect(() => {
    const fire = (e) => {
      if (e?.detail?._replay) return
      if (Date.now() - (window.__nextepClapAt || 0) < 900) return
      if (document.documentElement.getAttribute('data-reduce-motion') === '1') return
      clearTimeout(t.current); setOn(false)
      requestAnimationFrame(() => { setOn(true); t.current = setTimeout(() => setOn(false), CLAP_MS + 300) })
    }
    window.addEventListener('nextep:progress-marked', fire)
    return () => { window.removeEventListener('nextep:progress-marked', fire); clearTimeout(t.current) }
  }, [])
  if (!on) return null
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 'calc(var(--z-sheet) + 2)', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 132, height: 132, borderRadius: 34, background: 'rgba(4,4,6,0.62)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(var(--ink-rgb),0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', filter: 'drop-shadow(0 16px 36px rgba(0,0,0,0.6))', '--nxa': `clapStage ${CLAP_MS + 300}ms ease-out both` }}>
        <Clapboard size={80} watched slamOnMount accent="var(--amber)" idle="rgba(var(--ink-rgb),0.75)" />
      </div>
    </div>
  )
}

// ── MoonRiseLayer: the moon's moment, the way the clapboard has its slam ───────
// On nextep:plan-added a slate floats mid-screen and the moon rises into it, flips
// active (MoonIcon's own ring burst and star jitter), holds, and fades. ~1.2s, the
// same stage as ClapSlamLayer so the two objects read as a pair.
export function MoonRiseLayer() {
  const [on, setOn] = useState(false)
  const [lit, setLit] = useState(false)
  const t = useRef([])
  useEffect(() => {
    const fire = () => {
      if (document.documentElement.getAttribute('data-reduce-motion') === '1') return
      t.current.forEach(clearTimeout); t.current = []
      setOn(false); setLit(false)
      requestAnimationFrame(() => {
        setOn(true)
        t.current.push(setTimeout(() => setLit(true), 260))
        t.current.push(setTimeout(() => { setOn(false); setLit(false) }, CLAP_MS + 300))
      })
    }
    window.addEventListener('nextep:plan-added', fire)
    return () => { window.removeEventListener('nextep:plan-added', fire); t.current.forEach(clearTimeout) }
  }, [])
  if (!on) return null
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 'calc(var(--z-sheet) + 2)', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 132, height: 132, borderRadius: 34, background: 'rgba(4,4,6,0.62)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(var(--ink-rgb),0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', filter: 'drop-shadow(0 16px 36px rgba(0,0,0,0.6))', '--nxa': `clapStage ${CLAP_MS + 300}ms ease-out both` }}>
        <div style={{ display: 'flex', '--nxa': 'moonRise 0.7s cubic-bezier(0.34,1.3,0.64,1) both', color: lit ? 'var(--amber)' : 'rgba(var(--ink-rgb),0.6)', transition: 'color 260ms ease-out' }}>
          <MoonIcon size={72} color="currentColor" active={lit} />
        </div>
      </div>
    </div>
  )
}

export function SlamClapboard({ size = 72, delay = 350, accent = 'var(--amber)' }) {
  const [slam, setSlam] = useState(false)
  useEffect(() => { const t = setTimeout(() => setSlam(true), delay); return () => clearTimeout(t) }, [delay])
  return <Clapboard size={size} watched={slam} accent={accent} idle="rgba(var(--ink-rgb),0.7)" />
}
